import React from 'react';
import { getAuthCookie } from '../utils/cookies';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import ClockoutController from '../core/clockout.controller';
import { resolveAccessiblePath } from '../utils/resolveAccessiblePath';
import { useSessionHydrated } from '../hooks/useSessionHydrated';
import { useScopes } from '../hooks/useScope';
import Loading from '../components/common/Loading';

import Login from '../components/login/login';
import Signup from '../components/signup/signup';
import ResetPassword from '../components/reset-password/reset-password';
import Dashboard from '../components/dashboard/dashboard';
import Ledger from '../components/ledger/ledger';
import Reconciliation from '../components/reconciliation/reconciliation';
import User from '../components/users/user/user';
import Treasury from '../components/treasury/treasury';
import Team from '../components/team/team';
import Billing from '../components/billing/billing';
import Account from '../components/account/account';
import Reports from '../components/reports/reports';
import Reviews from '../components/reviews/reviews';
import ReviewDetailPage from '../components/reviews-detail/ReviewDetailPage';
import Configuration from '../components/configurations/configuration';
import Support from '../components/support/support';
import Ticket from '../components/support/ticket';
import Roles from '../components/roles/roles';
import OpenSsoView from '../components/open-sso/OpenSsoView';
import HasScope from '../components/common/hasScope/HasScope';
import { SCOPES } from '../constants/scopes.constants';
import UploadsSimulator from '../components/uploads-simulator/uploads-simulator';
import Notifications from '../components/notifications/notifications';

// Create a wrapper component that handles authentication
const ProtectedRoute = ({ children }) => {
    const isAuthenticated = getAuthCookie() !== 'expired';
    console.log('isAuthenticated', isAuthenticated);
    return isAuthenticated ? children : <Navigate to="/" />;
};

const PublicRoute = ({ children }) => {
    const isAuthenticated = getAuthCookie() !== 'expired';
    // On a fresh page load DataController hasn't hydrated the session yet
    // (nothing eagerly authenticates before routes render), so `scopes`
    // would read as empty and misroute to /account — wait for the fetch
    // to resolve before deciding.
    const hydrated = useSessionHydrated();
    const scopes = useScopes();

    if (!isAuthenticated) {
        return children;
    }
    if (!hydrated) {
        return <Loading />;
    }
    // Land on the first page the session's scopes actually grant, not a
    // hardcoded /dashboard — a role without screen:dashboard:view would
    // otherwise bounce straight into a 403.
    return <Navigate to={resolveAccessiblePath(scopes)} />;
};

/**
 * Route-level scope gate. Defers to <Loading/> until the session has hydrated
 * so <HasScope> never judges the empty scope list a cold load starts with —
 * without this, landing directly on a guarded deep link bounces the user out
 * even when they hold the scope. Denied users go to the first page their
 * scopes actually grant, not a hardcoded /dashboard.
 *
 * Belongs inside <ProtectedRoute>, which owns the unauthenticated case.
 */
const ScopeGuardedRoute = ({ scope, children }) => {
    const hydrated = useSessionHydrated();
    const scopes = useScopes();

    return (
        <HasScope
            scope={scope}
            behavior="redirect"
            redirectTo={resolveAccessiblePath(scopes)}
            isLoading={!hydrated}
        >
            {children}
        </HasScope>
    );
};


export default class ClockoutRouter extends React.Component {
    state = { authVersion: 0 }; // Dummy state to force re-renders

    componentDidMount() {
        // Subscribe to auth changes
        ClockoutController._notifyAuthChange = () => {
            this.setState((prev) => ({ authVersion: prev.authVersion + 1 }));
        };
    }

    render() {
        const router = createBrowserRouter([
            {
                path: '/open-sso/:clientId',
                element: (
                    <ProtectedRoute>
                        <ScopeGuardedRoute scope={SCOPES.OPENSSO_ENABLED}>
                            <OpenSsoView />
                        </ScopeGuardedRoute>
                    </ProtectedRoute>
                )
            },
            {
                path: '/',
                element: (
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                )
            },
            {
                path: '/signup',
                element: (
                    <PublicRoute>
                        <Signup />
                    </PublicRoute>
                )
            },
            {
                path: '/reset-password',
                element: (
                    <PublicRoute>
                        <ResetPassword />
                    </PublicRoute>
                )
            },
            {
                path: '/dashboard',
                element: (
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                )
            },
            {
                path: '/users',
                element: (
                    <ProtectedRoute>
                        <Team />
                    </ProtectedRoute>
                )
            },
            {
                path: '/ledger',
                element: (
                    <ProtectedRoute>
                        <Ledger />
                    </ProtectedRoute>
                )
            },
            {
                path: '/team',
                element: (
                    <ProtectedRoute>
                        <Team />
                    </ProtectedRoute>
                )
            },
            {
                path: '/reconciliation',
                element: (
                    <ProtectedRoute>
                        <Reconciliation />
                    </ProtectedRoute>
                )
            },
            {
                path: '/configurations',
                element: (
                    <ProtectedRoute>
                        <Configuration />
                    </ProtectedRoute>
                )
            },

            {
                path: '/users/user',
                element: (
                    <ProtectedRoute>
                        <User />
                    </ProtectedRoute>
                )
            },
            {
                path: '/treasury',
                element: (
                    <ProtectedRoute>
                        <Treasury />
                    </ProtectedRoute>
                )
            },
            {
                path: '/billing',
                element: (
                    <ProtectedRoute>
                        <Billing />
                    </ProtectedRoute>
                )
            },
            {
                path: '/account',
                element: (
                    <ProtectedRoute>
                        <Account />
                    </ProtectedRoute>
                )
            },
            {
                path: '/reports',
                element: (
                    <ProtectedRoute>
                        <Reports />
                    </ProtectedRoute>
                )
            },
            {
                path: '/reviews',
                element: (
                    <ProtectedRoute>
                        <Reviews />
                    </ProtectedRoute>
                )
            },
            {
                path: '/uploads-simulator',
                element: (
                    <ProtectedRoute>
                        <UploadsSimulator />
                    </ProtectedRoute>
                )
            },
            {
                path: '/reviews/:eventId',
                element: (
                    <ProtectedRoute>
                        <ReviewDetailPage />
                    </ProtectedRoute>
                )
            },
            {
                path: '/support',
                element: (
                    <ProtectedRoute>
                        <Navigate to="/support/ticket" replace />
                    </ProtectedRoute>
                )
            },
            {
                path: '/support/ticket/:ticketId',
                element: (
                    <ProtectedRoute>
                        <Ticket />
                    </ProtectedRoute>
                )
            },
            {
                path: '/support/ticket',
                element: (
                    <ProtectedRoute>
                        <Support />
                    </ProtectedRoute>
                )
            },
            {
                path: '/notifications',
                element: (
                    <ProtectedRoute>
                        <Notifications />
                    </ProtectedRoute>
                )
            },
            {
                path: '/roles',
                element: (
                    <ProtectedRoute>
                        <Roles />
                    </ProtectedRoute>
                )
            }
        ]);
        return <RouterProvider router={router} />;
    }
}
